import type { Occupants, Period, Property, Tenancy, Unit } from '@immoaktas/api-client';
import { floorSortKey, formatLocale, num } from '@immoaktas/shared';
import type { BuildingKind } from '../../../ui';

/** Shared wording and small calculations of the property and unit screens (design/Objekte … Wohnung). */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

export const addressLine = (p: Pick<Property, 'address'>) => [p.address.street, [p.address.zip, p.address.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
export const cityLine = (p: Pick<Property, 'address'>) => [p.address.zip, p.address.city].filter(Boolean).join(' ');
export const areaText = (n: number | null | undefined, minDigits = 0) => (n == null ? '–' : `${minDigits ? n.toLocaleString(formatLocale(), { minimumFractionDigits: minDigits, maximumFractionDigits: 2 }) : num(n, 2)} m²`);
export const sumArea = (units: Pick<Unit, 'current' | 'kind'>[]) => units.filter((u) => u.kind !== 'storage' && u.kind !== 'parking').reduce((s, u) => s + (u.current.area ?? 0), 0);

/** Short month names from i18n (`objects.months`, comma separated, January first). */
export const monthNames = (t: TFn) => t('objects.months').split(',');
/** "Jan–Dez" / "Jul–Jun" from the period start 'MM-DD'. */
export const periodMonths = (t: TFn, start: string) => {
  const m = monthNames(t);
  const i = Math.max(0, Math.min(11, Number(start.slice(0, 2)) - 1));
  return `${m[i]}–${m[(i + 11) % 12]}`;
};
/** Period label like the designs: "2025" for calendar years, "2025/26" otherwise, "2023 (Rumpf)" for short ones. */
export const periodLabel = (t: TFn, p: Pick<Period, 'from' | 'to' | 'isShortPeriod'>) => {
  const y1 = p.from.slice(0, 4);
  const y2 = p.to.slice(0, 4);
  const base = y1 === y2 ? y1 : `${y1}/${y2.slice(2)}`;
  return p.isShortPeriod ? t('objects.shortPeriod', { label: base }) : base;
};
/** "01.01." / "01/01" (day and month in the UI language). */
export const dayMonth = (d: string) => {
  const [y = 1970, m = 1, day = 1] = d.slice(0, 10).split('-').map(Number);
  return new Intl.DateTimeFormat(formatLocale(), { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(Date.UTC(y, m - 1, day));
};
/** "01.01.–31.12." (day and month only). */
export const dayMonthRange = (from: string, to: string) => `${dayMonth(from)}–${dayMonth(to)}`;

/** Card illustration per property type and size. */
export const buildingKind = (p: Pick<Property, 'type' | 'groups'>, units: number): BuildingKind => (p.type === 'weg_unit' ? 'flat' : units >= 24 || buildingGroups(p).length > 1 ? 'block' : 'house');

/**
 * Groups marked „ist ein Gebäude“ that hold units (docs/15 #40): only these become building cards in the house view.
 * Groups made only for the cost split stay out of it.
 */
/** Groups shown as buildings. Groups saved before the flag existed (`isBuilding` unset) keep the earlier rule: from two groups on, all are buildings. */
export const isBuildingGroup = (g: Property['groups'][number], all: Property['groups']) => g.isBuilding ?? all.length >= 2;
export const buildingGroups = (p: Pick<Property, 'groups'>) => p.groups.filter((g) => isBuildingGroup(g, p.groups) && g.unitIds.length);

/** "2 Erw · 1 Kind · 1 Baby" (compact, like the house cards). */
export const occupantsShort = (t: TFn, o: Occupants | null | undefined) => {
  if (!o) return '–';
  const parts = [t('objects.occ.adults', { count: o.adults })];
  if (o.children) parts.push(t('objects.occ.children', { count: o.children }));
  if (o.babies) parts.push(t('objects.occ.babies', { count: o.babies }));
  return parts.join(' · ');
};
export const personsText = (t: TFn, n: number) => t('objects.persons', { count: n });
/** What changed between two occupant counts, e.g. "+1 Baby" or "−1 Erw · +1 Kind"; null when equal. */
export const occupantsDiff = (t: TFn, prev: Occupants | null | undefined, next: Occupants | null | undefined) => {
  if (!prev || !next) return null;
  const parts = (['adults', 'children', 'babies'] as const).flatMap((k) => {
    const d = (next[k] ?? 0) - (prev[k] ?? 0);
    return d ? [`${d > 0 ? '+' : '−'}${t(`objects.occ.${k}`, { count: Math.abs(d) })}`] : [];
  });
  return parts.length ? parts.join(' · ') : null;
};

/** "Markus Schmidt" → "M. Schmidt"; families, companies and multi word names with "&" stay as they are. */
export const shortName = (name: string) => {
  const n = name.trim();
  if (/^(Familie|Family)\s|GmbH|GbR|\bAG\b|&|\be\.\s?V\./.test(n)) return n;
  const parts = n.split(/\s+/);
  if (parts.length < 2) return n;
  return `${parts[0]![0]}. ${parts[parts.length - 1]}`;
};
const lastName = (name: string) => name.replace(/^(Familie|Family)\s+/, '').trim().split(/\s+/).pop() ?? name;
/** Surnames only ("Becker", "Arslan · Brandt · Okafor") for change lines. */
export const surnameLine = (names: string[] | undefined) => (names ?? []).filter(Boolean).map((n) => (/GmbH|GbR|&/.test(n) ? n : lastName(n))).join(' · ');
/** Party line: one party → short name; several → "WG Arslan · Brandt · Okafor" for shared flats, else last names. */
export const partyLine = (t: TFn, names: string[] | undefined, kind?: string) => {
  const list = (names ?? []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return shortName(list[0]!);
  const joined = list.map(lastName).join(' · ');
  return kind === 'shared_flat' ? t('objects.sharedFlat', { names: joined }) : joined;
};

/** Floor label of a unit ("EG"), with position ("EG links"). */
export const floorOf = (p: Pick<Property, 'floors'>, u: Pick<Unit, 'floorId'>) => p.floors.find((f) => f.id === u.floorId)?.label ?? '';
export const placeOf = (p: Pick<Property, 'floors'>, u: Pick<Unit, 'floorId' | 'position'>) => [floorOf(p, u), u.position].filter(Boolean).join(' ');
/**
 * Units grouped by floor like the house drawing (top floor first, units without a floor after the upper floors, cellar
 * last), keeping the order inside a floor as the server sends it (sortIndex, position, label: compareHouseOrder).
 */
export const byFloor = <T extends { floorId?: string | null }>(p: Pick<Property, 'floors'>, units: readonly T[]): T[] =>
  [...units].sort((a, b) => floorSortKey(p.floors, a.floorId) - floorSortKey(p.floors, b.floorId));
export const groupOf = (p: Pick<Property, 'groups'>, unitId: string) => p.groups.find((g) => g.unitIds.includes(unitId))?.label;

/** Active (not corrected) slices of a list, sorted. */
export const liveSlices = <T,>(l: { value: T; validFrom: string; validTo: string | null; supersededBy?: string; id: string }[]) => l.filter((s) => !s.supersededBy).sort((a, b) => a.validFrom.localeCompare(b.validFrom));
/** Value of a slice list at a date. */
export const sliceAt = <T,>(l: { value: T; validFrom: string; validTo: string | null; supersededBy?: string }[], d: string) => l.find((s) => !s.supersededBy && s.validFrom <= d && (!s.validTo || s.validTo >= d))?.value;
/** Earlier value (the slice before the current one), e.g. "bis 30.06.2025: 52 m²". */
export const previousSlice = <T,>(l: { value: T; validFrom: string; validTo: string | null; supersededBy?: string; id: string }[], d: string) => {
  const live = liveSlices(l);
  const i = live.findIndex((s) => s.validFrom <= d && (!s.validTo || s.validTo >= d));
  return i > 0 ? live[i - 1] : undefined;
};

/** Monthly target of a tenancy (rent + prepayments). */
export const monthlyTarget = (c: Tenancy['current']) => (c.rentColdCents ?? 0) + (c.prepaymentOpsCents ?? 0) + (c.prepaymentHeatCents ?? 0);
export const prepayments = (c: Tenancy['current']) => (c.prepaymentOpsCents ?? 0) + (c.prepaymentHeatCents ?? 0);

/** Day fraction of a date inside [from, to] (for time charts). */
export const fraction = (d: string, from: string, to: string) => {
  const a = Date.parse(from);
  const span = Date.parse(to) + 86_400_000 - a;
  return Math.max(0, Math.min(1, (Date.parse(d) - a) / span));
};
/** End fraction of a date (end of that day). */
export const fractionEnd = (d: string, from: string, to: string) => fraction(new Date(Date.parse(d) + 86_400_000).toISOString().slice(0, 10), from, to);
export const addDaysIso = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** How far ahead a move-in or a change of tenant tints the unit yellow ("Einzug bald"). */
export const SOON_DAYS = 92;
/**
 * Occupancy of a unit on `day` for the tile tint (house drawing, object cards; docs/08 "Belegung einer Einheit"):
 * soon (a tenancy starts within SOON_DAYS, also a change of tenant) · rented · own (owner occupied) · off (renovation,
 * not rentable) · vacant. `tenancies`: those of this unit.
 */
export type OccupancyState = 'rented' | 'soon' | 'vacant' | 'own' | 'off';
export function occupancyState(status: string | undefined, tenancies: Pick<Tenancy, 'start' | 'end'>[], day: string): OccupancyState {
  const horizon = addDaysIso(day, SOON_DAYS);
  if (tenancies.some((x) => x.start > day && x.start <= horizon)) return 'soon';
  if (tenancies.some((x) => x.start <= day && (!x.end || x.end >= day))) return 'rented';
  if (status === 'owner_occupied') return 'own';
  if (status === 'renovation' || status === 'not_rentable') return 'off';
  return 'vacant';
}

/** Display unit of a meter ("m³", "kWh", "Einh."). */
export const unitSymbol = (t: TFn, u: string) => (u === 'm3' || u === 'm³' ? 'm³' : u === 'units' || u === 'Einheiten' ? t('meterOverview.unitsShort') : u);

// ───── billing rhythm (Zeiträume, ObjektAnlegen) ─────
export type Rhythm = 'calendar' | 'july' | 'october' | 'custom';
export const PRESET: Record<Exclude<Rhythm, 'custom'>, string> = { calendar: '01-01', july: '07-01', october: '10-01' };
export const rhythmOf = (start: string): Rhythm => (Object.entries(PRESET).find(([, v]) => v === start)?.[0] as Rhythm | undefined) ?? 'custom';
/** "15.03." ↔ "03-15"; null when not a real day (29.02. is not allowed as period start). */
export const toMonthDay = (s: string) => {
  const m = /^\s*(\d{1,2})\.(\d{1,2})\.?\s*$/.exec(s);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12 || d < 1 || d > new Date(Date.UTC(2023, mo, 0)).getUTCDate()) return null;
  return `${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};
export const fromMonthDay = (md: string) => `${md.slice(3, 5)}.${md.slice(0, 2)}.`;
/** The billing period (start "MM-DD") that contains `date`: { from, to } inclusive. */
export const periodAround = (start: string, date: string) => {
  const y = Number(date.slice(0, 4));
  const from = `${date.slice(5) >= start ? y : y - 1}-${start}`;
  const to = addDaysIso(`${Number(from.slice(0, 4)) + 1}-${start}`, -1);
  return { from, to };
};
